import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, Phone, Mail } from "lucide-react";

const LINKS = [
  { label: "Chi Siamo", hash: "#manifesto", testid: "nav-link-manifesto" },
  { label: "Servizi", hash: "#funzionalita", testid: "nav-link-features" },
  { label: "Lavori", hash: "#lavori", testid: "nav-link-lavori" },
  { label: "Preventivi", hash: "#prezzi", testid: "nav-link-pricing" },
  { label: "Contatti", hash: "#contatti", testid: "nav-link-contatti" },
];

export const scrollToHash = (hash) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(hash, { offset: -72 });
  } else {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  }
};

export default function Navbar({ onEarlyAccess }) {
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (hash) => {
    setMenuOpen(false);
    setTimeout(() => scrollToHash(hash), 80);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className="sticky top-0 z-50 border-b border-[#D9E4F5] bg-white/85 backdrop-blur-xl"
    >
      <div className="border-b border-[#2A5290] bg-neon">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-4 px-4 py-1.5 sm:justify-between sm:px-8 lg:px-16">
          <a
            data-testid="topbar-phone"
            href="tel:+393755398236"
            className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.08em] text-white transition-opacity duration-300 hover:opacity-80 sm:text-[11px]"
          >
            <Phone className="h-3 w-3 shrink-0" />
            375 539 8236
          </a>
          <a
            data-testid="topbar-email"
            href="mailto:HELPLYSRLS.LE@GMAIL.COM"
            className="flex items-center gap-1.5 truncate font-mono text-[10px] tracking-[0.08em] text-white transition-opacity duration-300 hover:opacity-80 sm:text-[11px]"
          >
            <Mail className="h-3 w-3 shrink-0" />
            HELPLYSRLS.LE@GMAIL.COM
          </a>
        </div>
      </div>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8 lg:px-16">
        <button
          data-testid="nav-logo"
          onClick={() => {
            setMenuOpen(false);
            window.__lenis?.scrollTo(0);
          }}
          className="flex items-center gap-3"
        >
          <img
            src="/logo.png"
            alt="Logo HELPLY SRLS"
            className="h-11 w-11 rounded-full border border-[#D9E4F5] bg-white object-cover shadow-sm"
          />
          <span className="font-display text-sm font-bold tracking-[0.2em] text-[#16345F]">
            HELPLY<span className="ml-1.5 text-[10px] text-neon">SRLS</span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.hash}
              data-testid={l.testid}
              onClick={() => scrollToHash(l.hash)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-slate-500 transition-colors duration-300 hover:text-neon"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-neon transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-button"
            onClick={onEarlyAccess}
            className="group hidden items-center gap-1.5 rounded-full bg-neon px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-white transition-all duration-300 hover:shadow-[0_10px_30px_rgba(53,101,174,0.45)] sm:flex"
          >
            Preventivo Gratuito
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Apri menu"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#D9E4F5] text-[#16345F] transition-colors duration-300 hover:border-neon/50 hover:text-neon lg:hidden"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-[#D9E4F5] bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.hash}
                  data-testid={`mobile-link-${l.hash.slice(1)}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.3 }}
                  onClick={() => go(l.hash)}
                  className="flex items-center justify-between rounded-lg px-4 py-3 text-left font-mono text-sm uppercase tracking-[0.2em] text-slate-600 transition-colors duration-300 hover:bg-[#EEF4FC] hover:text-neon"
                >
                  {l.label}
                  <ArrowUpRight className="h-4 w-4 text-neon/60" />
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-cta-button"
                initial={{ opacity: 0, x: -16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.08 + LINKS.length * 0.06, duration: 0.3 }}
                onClick={() => {
                  setMenuOpen(false);
                  onEarlyAccess?.();
                }}
                className="mt-2 rounded-full bg-neon px-4 py-3.5 font-display text-xs font-bold uppercase tracking-[0.15em] text-white"
              >
                Preventivo Gratuito
              </motion.button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
